<template>
  <div>
    <div class="setting-header">
      <app-btn-submit :is-submitting="isUpdating" :css="'btn btn-primary btn-sm'" :text="this.$t('app.update')"
        @on-click="save()" />
    </div>
    <div class="setting-page-content form-group company-setting card-container-no-scroll">
      <div class="card">
        <div class="row">
          <div class="col-lg-6">
            <h5 class="mb-4">{{ $t('user.companyInformation') }}</h5>
            <app-form ref="infoForm" @on-submit="onSubmit" :schema="infoSchema" :layout="infoLayout"
              :data="formModel" />
            <h5 class="mt-4 mb-4">{{ $t('user.subscriptionInformation') }}</h5>
            <app-form ref="subDataForm" :schema="subDataSchema" :layout="subDataLayout" :data="formModel" />
          </div>
          <div class="col-lg-6">
            <h5 class="mb-4">{{ $t('user.repInformation') }}</h5>
            <app-form ref="repInfoForm" :schema="repInfoSchema" :layout="repInfoLayout" :data="formModel" />
            <h5 style="margin-top: 3rem; margin-bottom: 1.5rem">{{ $t('user.bankInformation') }}</h5>
            <app-form ref="bankInfoForm" :schema="bankInfoSchema" :layout="bankInfoLayout" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import AppForm from '@/components/app-form/';
import AppBtnSubmit from '@/components/app-button/index';
import SellerProxy from '@/store/proxies/e-invoice/seller-proxy';
import ToastUtils from '@/utils/toast';
import { regexNationalId, regexPassportNo, regexBusinessTaxCode } from '@/utils/regex';
import DateTimeUtils from '@/utils/datetime';

const sellerProxy = new SellerProxy()

const customAllSettled = Promise.allSettled || ((promises) => Promise.all(
  promises.map(p => p
    .then(value => ({
      status: "fulfilled",
      value
    }))
    .catch(reason => ({
      status: "rejected",
      reason
    }))
  )
));

export default {
  name: 'CompanySetting',
  components: {
    AppBtnSubmit,
    AppForm,
  },
  data() {
    return {
      formModel: {
        name: null,
        full_name: null,
        tax_code: null,
        seller_fax_number: null,
        seller_email: null,
        company_address: null,
        total_capacity: null,
        count_published_invoice: null,
        seller_description: null,
        seller_website: null,
        // seller_representative: null,
        // seller_representative_role: null,
        // seller_representative_phone: null,
        // seller_bank_account: null,
        // seller_bank_account_owner: null,
        // seller_bank_name: null,
        // seller_bank_branch: null
      },
      infoSchema: [
        {
          name: 'name',
          label: this.$t('user.name'),
          placeholder: `${this.$t('app.enter')} ${this.$t('user.name')}`,
          type: 'input',
          validation: 'required',
        },
        {
          name: 'full_name',
          label: this.$t('user.companyFullName'),
          placeholder: `${this.$t('app.enter')} ${this.$t('user.companyFullName')}`,
          type: 'input',
          validation: 'required',
        },
        {
          name: 'tax_code',
          label: this.$t('user.taxCode'),
          placeholder: `${this.$t('app.enter')} ${this.$t('user.taxCode')}`,
          inputType: 'text',
          type: 'input',
          disabled: true,
          validation: { required: true, regex: regexBusinessTaxCode },
        },
        {
          name: 'company_address',
          label: this.$t('user.companyAddress'),
          placeholder: `${this.$t('app.enter')} ${this.$t('user.companyAddress')}`,
          type: 'input',
          validation: 'required',
        },
        {
          name: 'seller_fax_number',
          label: this.$t('user.faxNumber'),
          type: 'input',
          validation: {
            regex: /^(|[0-9\s-]{9,15})$/
          }
        },
        {
          name: 'seller_phone',
          label: this.$t('user.phone'),
          type: 'input',
          validation: {
            required: true,
            regex: /^[0-9]{9,11}$/
          },
        },
        {
          name: 'seller_email',
          label: "Email",
          type: 'input',
          validation: 'required',
        },
        {
          name: 'seller_description',
          label: this.$t('user.description'),
          type: 'input'
        },
        {
          name: 'seller_website',
          label: 'Website',
          type: 'input'
        }
      ],
      subDataSchema: [
        {
          name: 'total_capacity',
          label: this.$t('user.total_capacity'),
          inputType: 'text',
          type: 'input',
          readonly: true,
        },
        {
          name: 'count_published_invoice',
          label: this.$t('user.count_published_invoice'),
          inputType: 'text',
          type: 'input',
          readonly: true,
        }
      ],
      repInfoSchema: [
        {
          name: 'seller_representative',
          label: this.$t('user.representative'),
          type: 'input',
          validation: 'required'
        },
        {
          name: 'seller_representative_role',
          label: this.$t('user.representativeRole'),
          type: 'input'
        },
        {
          name: 'seller_representative_phone',
          label: this.$t('user.representativePhone'),
          type: 'input',
          validation: {
            regex: /^[0-9]{9,11}$/
          }
        },
        {
          name: 'seller_representative_email',
          label: 'Email',
          type: 'input',
        },
        {
          name: 'seller_representative_id_no',
          label: this.$t('user.repId'),
          type: 'input',
          validation: { required: true, regex: regexNationalId },
        },
        {
          name: 'ddpl_passport_no',
          label: this.$t('user.passportNo'),
          type: 'input',
          validation: { regex: regexPassportNo },
        },
        {
          name: 'ddpl_birthday',
          label: this.$t('user.dob'),
          type: 'datepicker',
          validation: 'required'
        },
        {
          name: 'ddpl_sex',
          label: this.$t('user.gender'),
          type: 'select',
          validation: 'required',
          options: [
            { text: this.$t('user.genderLabels.male'), value: 'MALE' },
            { text: this.$t('user.genderLabels.female'), value: 'FEMALE' },
          ],
          defaultValue: 'FEMALE'
        },
      ],
      bankInfoSchema: [
        {
          name: 'seller_bank_account',
          label: this.$t('user.bankAccount'),
          type: 'input'
        },
        {
          name: 'seller_bank_account_owner',
          label: this.$t('user.bankAccountOwner'),
          type: 'input'
        },
        {
          name: 'seller_bank_name',
          label: this.$t('user.bankName'),
          type: 'input'
        },
        {
          name: 'seller_bank_branch',
          label: this.$t('user.bankBranch'),
          type: 'input'
        }
      ],
      infoLayout: {
        'div:1': {
          field: 'name',
        },
        'div:2': {
          field: 'full_name',
        },
        'div:3': {
          field: 'tax_code',
        },
        'div:4': {
          field: 'company_address',
        },
        'div:5': {
          field: 'seller_fax_number',
        },
        'div:6': {
          field: 'seller_phone',
        },
        'div:7': {
          field: 'seller_email',
        },
        'div:8': {
          field: 'seller_description',
        },
        'div:9': {
          field: 'seller_website',
        },
      },
      subDataLayout: {
        'div:1': {
          field: 'total_capacity',
        },
        'div:2': {
          field: 'count_published_invoice',
        },
      },
      repInfoLayout: {
        'div:1': {
          field: 'seller_representative',
        },
        'div:2': {
          field: 'seller_representative_role',
        },
        'div:3': {
          field: 'seller_representative_phone',
        },
        'div:4': {
          field: 'seller_representative_email',
        },
        'div:5': {
          field: 'seller_representative_id_no',
        },
        'div:6': {
          field: 'ddpl_passport_no',
        },
        'div:7': {
          field: 'ddpl_birthday',
        },
        'div:8': {
          field: 'ddpl_sex',
        }
      },
      bankInfoLayout: {
        'div:1': {
          field: 'seller_bank_account',
        },
        'div:2': {
          field: 'seller_bank_account_owner',
        },
        'div:3': {
          field: 'seller_bank_name',
        },
        'div:4': {
          field: 'seller_bank_branch',
        }
      },
      isUpdating: false,
    };
  },
  created() {
    this.fetchSellerInformation()
  },
  methods: {
    ...mapActions('auth', ['updateCompanyInfo']),

    fetchSellerInformation() {
      sellerProxy
        .getSellerInformation()
        .then((res) => {
          if (res) {
            const { name, full_name, tax_code, company_address, total_capacity, count_published_invoice, seller_fax_number, seller_email, seller_phone, seller_representative, seller_representative_role, seller_representative_phone, seller_representative_email, seller_bank_account, seller_bank_account_owner, seller_bank_name, seller_bank_branch, seller_description, seller_website,
              seller_representative_id_no, ddpl_passport_no, ddpl_birthday, ddpl_sex
            } = res;
            this.formModel = res;
            this.$refs.infoForm.setData({
              name, full_name, tax_code, company_address, seller_fax_number, seller_email, seller_phone, seller_description, seller_website
            })
            this.$refs.subDataForm.setData({
              total_capacity, count_published_invoice
            })
            this.$refs.repInfoForm.setData({
              seller_representative, seller_representative_role, seller_representative_phone, seller_representative_email,
              seller_representative_id_no, ddpl_passport_no, ddpl_birthday, ddpl_sex: ddpl_sex || 'FEMALE'
            })
            this.$refs.bankInfoForm.setData({
              seller_bank_account, seller_bank_account_owner, seller_bank_name, seller_bank_branch
            })
          }
        }).catch((error) => {
          ToastUtils.showToast('error', error.message || error);
        });
    },
    /*
    Example payload for the method below
    {"seller_name":"Công ty TNHH SEVEN","seller_tax_code":"0440970073","seller_street_address":"2 Đ. Hải Triều, Bến Nghé, Quận 1, Hồ Chí Minh","seller_phone":"0942193241","seller_fax_number":null,"seller_email":"baynguyen2000@gmail.com","seller_website":null,"seller_description":"Công ty 2 người","seller_representative":"baynguyen2000@gmail.com","seller_bank_account":"2323243530940","seller_bank_account_owner":"NGUYEN VAN BAY","seller_bank_name":"MB - NH TMCP QUAN DOI","seller_bank_branch":"CN Tay Ho","seller_postal_code":null,"company_id":198,"seller_contact_persion_name":null,"seller_signed_persion_name":null,"seller_submitted_persion_name":null,"seller_representative_email":"baynguyen1997@gmail.com","seller_representative_phone":"0364924043","seller_representative_role":"CEO","seller_search_invoice_link":null}
    */
    processPayloadForApi(formModel) {
      const infoValues = this.$refs.infoForm.getValues();
      const repInfoValues = this.$refs.repInfoForm.getValues()
      const bankInfoValues = this.$refs.bankInfoForm.getValues()
      return {
        seller_name: infoValues.name,
        seller_full_name: infoValues.full_name,
        seller_tax_code: infoValues.tax_code,
        seller_street_address: infoValues.company_address,
        seller_phone: infoValues.seller_phone,
        seller_fax_number: infoValues.seller_fax_number,
        seller_email: infoValues.seller_email,
        seller_website: infoValues.seller_website,
        seller_description: infoValues.seller_description,
        ...repInfoValues,
        ddpl_birthday: DateTimeUtils.formatDate(repInfoValues.ddpl_birthday, 'YYYY-MM-DD'),
        ...bankInfoValues
      }
    },
    onSubmit() {
      const validations = customAllSettled([
        this.$refs.infoForm.validate(),
        this.$refs.repInfoForm.validate()
      ]);

      validations.then(results => {
        const isValid = results.every(r => r.value === true);

        if (!isValid) {
          const missing = this.getMissingRequiredFields();
          ToastUtils.showToast(
            'error',
            `${this.$t('app.enterCompleteData')}: ${missing.join(', ')}`
          );
          return;
        }

        this.isUpdating = true;
        const payload = this.processPayloadForApi();

        sellerProxy.updateSellerInformation(payload)
          .then(() => {
            ToastUtils.showToast('success', this.$t('app.companyInformationUpdated'));
          })
          .catch(err => {
            ToastUtils.showToast('error', err.message || err);
          })
          .finally(() => {
            this.isUpdating = false;
          });
      });
    },
    save() {
      this.$modal.show('dialog', {
        title: `${this.$t('app.confirm')} ${this.$t('app.update').toLowerCase()} ${this.$t('user.companyInformation').toLowerCase()}`,
        text: `${this.$t('app.editBusinessInform')}`,
        okText: this.$t('app.confirm'),
        okClass: 'btn btn-primary btn-sm',
        showLoading: true,
        onOk: async () => {
          if (this.isUpdating) {
            return;
          }
          this.$refs.infoForm.submit();
        },
      });
    },
    getMissingRequiredFields() {
      const missing = [];

      const infoValues = this.$refs.infoForm.getValues();
      const repValues = this.$refs.repInfoForm.getValues();

      const allSchemas = [...this.infoSchema, ...this.repInfoSchema];

      allSchemas.forEach(field => {
        const isRequired =
          field.validation === 'required' ||
          (typeof field.validation === 'object' && field.validation.required);

        if (!isRequired) return;

        const value =
          infoValues[field.name] !== undefined
            ? infoValues[field.name]
            : repValues[field.name];

        if (!value || value === '') {
          missing.push(field.label || field.name);
        }
      });

      return missing;
    }
  },
};
</script>
<style lang="scss">
.setting-page-content {
  h5 {
    color: #0078D4;
    font-size: 16px;
    font-weight: bold;
  }

  display: flex;
  flex-direction: column;
}

.setting-header {
  text-align: right;
  margin-top: auto;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.company-setting form {
  padding-right: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/portal-components/setting/company-setting.vue